import cx from 'classnames'
import {
  type ComponentPropsWithRef,
  type ReactNode,
  type SubmitEventHandler,
  useCallback,
  useId,
  useMemo,
} from 'react'
import type { FormControlProps } from '@/infra/prop-types'
import styles from './Form.module.css'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import flattenChildren from 'react-keyed-flatten-children'

export interface FormProps extends Omit<
  ComponentPropsWithRef<'form'>,
  'className' | 'onSubmit'
> {
  /**
   * Form submit handler.
   */
  onSubmit?: SubmitEventHandler

  /**
   * Reduces the gaps between fields.
   */
  compact?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * The Form itself.
 */
export function Form({
  ref,
  action = '#', // makes sure Safari shows a `go` button on the keyboard rather than `return`
  noValidate = true,
  onSubmit,
  compact,
  testId,
  children,
  ...props
}: FormProps) {
  const submit: SubmitEventHandler = useCallback(
    (e) => {
      if (onSubmit) {
        e.preventDefault()
        onSubmit(e)
      }
    },
    [onSubmit],
  )

  return (
    <form
      {...props}
      ref={ref}
      noValidate={noValidate}
      action={action}
      onSubmit={submit}
      data-test={testId}
      className={cx(styles.form, compact && styles.compact)}
    >
      {children}
    </form>
  )
}

export type FormFieldSetProps = Omit<ComponentPropsWithRef<'div'>, 'className'>

/**
 * Form Field Set
 */
export function FormFieldSet({ children, ref, ...props }: FormFieldSetProps) {
  return (
    <div {...props} ref={ref} className={styles.fieldSetLayout}>
      <fieldset className={styles.fieldSet}>{children}</fieldset>
    </div>
  )
}

interface FormFieldSetLegendProps {
  children?: ReactNode
  id?: string
  isSkeleton?: boolean
}

/**
 * Form Field Set Legend
 */
export function FormFieldSetLegend({
  isSkeleton,
  children,
  id,
}: FormFieldSetLegendProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <legend id={id} className={styles.fieldSetLegend}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </legend>
  )
}

interface FormFieldSetDescriptionProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Form Field Set Description
 */
export function FormFieldSetDescription({
  children,
  isSkeleton,
}: FormFieldSetDescriptionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <div className={styles.fieldSetDescription}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

export interface FormFieldGroupProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'className'
> {
  /**
   * Whether to stack elements on compact layouts.
   */
  stackWhenCompact?: boolean
}

/**
 * Group form fields, so they appear together (side by side).
 */
export function FormFieldGroup({
  children,
  ref,
  stackWhenCompact,
  ...props
}: FormFieldGroupProps) {
  return (
    <div
      {...props}
      ref={ref}
      className={cx(
        styles.fieldGroup,
        stackWhenCompact && styles.stackWhenCompact,
      )}
    >
      {children}
    </div>
  )
}

export type FormFieldProps = Omit<ComponentPropsWithRef<'div'>, 'className'>

/**
 * A field in the form.
 */
export function FormField({ children, ref, ...props }: FormFieldProps) {
  return (
    <div {...props} ref={ref} className={styles.field}>
      {children}
    </div>
  )
}

interface FormFieldLabelProps {
  children?: ReactNode

  /**
   * ID of the label.
   */
  id?: string

  /**
   * Which input (`name`) is the label for.
   */
  for?: string

  /**
   * Adds styling indicating that the field is invalid.
   */
  invalid?: boolean

  /**
   * Whether to render a skeleton instead of the label.
   */
  isSkeleton?: boolean
}

/**
 * A label for a form field.
 */
export function FormFieldLabel({
  children,
  invalid,
  isSkeleton,
  id,
  for: htmlFor,
}: FormFieldLabelProps) {
  return (
    <label
      id={id}
      htmlFor={htmlFor}
      className={cx(styles.label, invalid && styles.labelInvalid)}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </label>
  )
}

export interface FormFieldHintProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'className'
> {
  /**
   * Whether to render a skeleton instead of the hint.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Form Field Hint
 */
export function FormFieldHint({
  children,
  ref,
  id,
  isSkeleton,
  testId,
  ...props
}: FormFieldHintProps) {
  const isRendered = flattenChildren(children).length > 0
  if (!isRendered && !isSkeleton) {
    return null
  }

  return (
    <div
      {...props}
      ref={ref}
      id={id}
      data-test={testId}
      className={styles.fieldHint}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

export interface FormFieldErrorProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'className'
> {
  /**
   * Whether to render a skeleton instead of the error.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Form Field Error
 */
export function FormFieldError({
  children,
  ref,
  id,
  isSkeleton,
  testId,
  ...props
}: FormFieldErrorProps) {
  const isRendered = flattenChildren(children).length > 0
  if (!isRendered && !isSkeleton) {
    return null
  }

  return (
    <div
      {...props}
      ref={ref}
      id={id}
      data-test={testId}
      className={styles.fieldError}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

interface FormFieldDescriptionProps {
  children?: ReactNode
  id?: string

  /**
   * Whether the description should indicate invalidity.
   */
  invalid?: boolean

  /**
   * Whether to render a skeleton instead of the description.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Form Field Description
 */
export function FormFieldDescription({
  children,
  id,
  invalid,
  isSkeleton,
  testId,
}: FormFieldDescriptionProps) {
  // We don't want the description element to take up space if there's no content to
  // display.
  const isRendered = flattenChildren(children).length > 0

  return (
    <div
      id={id}
      data-test={testId}
      className={cx(
        styles.fieldDescription,
        invalid && styles.fieldDescriptionInvalid,
        !isRendered && styles.fieldDescriptionHidden,
      )}
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </div>
  )
}

/**
 * Provides information about the field (if any) a
 * component is contained within.
 */
interface FormFieldHookResult {
  /**
   * Props for the form field control.
   */
  control: FormControlProps
  /**
   * Props for the form field hint.
   */
  hint: Pick<FormFieldHintProps, 'id'>
  /**
   * Props for the form field error.
   */
  error: Pick<FormFieldErrorProps, 'id'>
  /**
   * Props for the form field label.
   */
  label: FormFieldLabelProps
  /**
   * Props for the form field description.
   */
  description: FormFieldDescriptionProps
}

export interface UseFormFieldProps {
  name?: string
  invalid?: boolean
  /**
   * Description content rendered for the field.
   */
  description?: ReactNode
  /**
   * Hint content rendered for the field.
   */
  hint?: ReactNode
  /**
   * Error content rendered for the field.
   */
  errorMessage?: ReactNode
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

/**
 * Manages props that are to be distributed on to a form
 * field label, control, description, and hint.
 */
export function useFormField({
  name,
  invalid,
  description,
  hint,
  errorMessage,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: UseFormFieldProps = {}): FormFieldHookResult {
  // ID of the field. Used by the control to link to the accessible description
  // if available.
  const fieldId = useId()

  // ID of the description
  const descriptionId = useId()

  // ID of the label. Used by the control to link to the accessible label.
  const labelId = useId()

  // ID of the hint.
  const hintId = useId()

  // ID of the error.
  const errorId = useId()

  const effectiveLabelledById = ariaLabelledBy || labelId
  const effectiveDescribedById =
    [
      flattenChildren(description).length > 0 ? descriptionId : null,
      flattenChildren(hint).length > 0 ? hintId : null,
      flattenChildren(errorMessage).length > 0 ? errorId : null,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  // Props to pass to controls.
  const controlProps = useMemo((): FormControlProps => {
    const isInvalid = Boolean(invalid)
    return {
      name: name,
      id: fieldId,
      invalid: isInvalid,
      'aria-describedby': effectiveDescribedById,
      // Only add the `labelledby` if no explicit label was passed.
      'aria-labelledby': ariaLabel ? undefined : effectiveLabelledById,
      'aria-label': ariaLabel,
    }
  }, [
    invalid,
    name,
    fieldId,
    ariaLabel,
    effectiveLabelledById,
    effectiveDescribedById,
  ])

  // Props to pass to the hint.
  const hintProps = useMemo(
    (): Pick<FormFieldHintProps, 'id'> => ({
      id: hintId,
    }),
    [hintId],
  )

  // Props to pass to the error.
  const errorProps = useMemo(
    (): Pick<FormFieldErrorProps, 'id'> => ({
      id: errorId,
    }),
    [errorId],
  )

  // Props for the label.
  const labelProps = useMemo(
    (): FormFieldLabelProps => ({
      for: fieldId,
      invalid: Boolean(invalid),
      id: labelId,
    }),
    [fieldId, invalid, labelId],
  )

  const descriptionProps = useMemo(
    (): FormFieldDescriptionProps => ({
      id: descriptionId,
      invalid: Boolean(invalid),
    }),
    [descriptionId, invalid],
  )

  // The form field context wires up the controls and the labels + hints
  // to be accessible and propagates validation state.
  return useMemo(
    (): FormFieldHookResult => ({
      control: controlProps,
      hint: hintProps,
      error: errorProps,
      label: labelProps,
      description: descriptionProps,
    }),
    [controlProps, hintProps, errorProps, labelProps, descriptionProps],
  )
}
